import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  InstancedBufferAttribute,
  InstancedMesh,
  NormalBlending,
  PlaneGeometry,
  ShaderMaterial,
} from 'three';
import { seededRandom } from './random.js';

// Volumetric nebula, v3 — billowy clusters, not dots (v1) or straight streaks
// (v2). Built from the 2026-07-13 research pass on real nebulae + realtime
// cloud rendering (MSFS-style pre-shaded puff sprites; Quilez domain warping;
// astrophoto palettes):
//   · CAULIFLOWER MASSES: puffs are rejection-sampled inside overlapping
//     ellipsoid "masses" (cluster placement makes billows — scattered single
//     puffs read as polka dots no matter how good the texture is).
//   · ROUNDNESS is baked into the puff texture: billow-FBM (sum of |noise|)
//     lobes, TOP-LIT via a 2-tap directional derivative — one global light
//     direction, mirror-safe in U, so every rim brightens toward the same
//     "ionizing star" (up).
//   · COLOR ZONES, muted, analogous-only overlaps (real nebulae zone hue by
//     ionization, never confetti): crimson/rose H-alpha body on the left
//     (agrees with the video's mauve), warm amber core glow center, dusty
//     OIII teal → deep indigo fringe on the right, maroon periphery. Low
//     per-sprite luminance lets additive accumulation build the cores.
//   · DARK DUST: a separate NON-additive layer of umber silhouette blobs
//     under the glow — dust lanes are half a nebula's identity.
//   · Curved TENDRILS: bezier ribbons modulated by ridged FBM, bright at the
//     attachment end, dissolving tips.
// All textures are procedural canvases (in-memory, zero cross-origin).
// Journey-only (reduced motion never mounts it); ~200 quads in 3 draw calls.

// ------------------------------------------------------------------ noise
function hash2(ix, iy, seed) {
  let h = Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(seed, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function vnoise(x, y, seed) {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy, seed);
  const b = hash2(ix + 1, iy, seed);
  const c = hash2(ix, iy + 1, seed);
  const d = hash2(ix + 1, iy + 1, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}
// billow: fold each octave's noise into ridges — cauliflower lobes
function billow(x, y, seed, octaves = 5) {
  let v = 0;
  let amp = 0.5;
  let f = 1;
  for (let o = 0; o < octaves; o++) {
    v += amp * Math.abs(2 * vnoise(x * f, y * f, seed + o * 101) - 1);
    amp *= 0.5;
    f *= 2;
  }
  return v; // ~[0,1)
}

// -------------------------------------------------------------- textures
function makeCanvas(w, h) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

// Pre-shaded billow puff: density in A, top-lit shading in R.
function puffTexture(seed, { lit = true, hard = false } = {}) {
  const size = 512;
  const canvas = makeCanvas(size, size);
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(size, size);
  const density = (u, v) => {
    const dx = (u - 0.5) * 2;
    const dy = (v - 0.5) * 2;
    const r2 = dx * dx + dy * dy;
    if (r2 > 1) return 0;
    const lobes = 1 - billow(u * 3.2, v * 3.2, seed); // invert: ridges out, lobes in
    const env = Math.pow(Math.max(0, 1 - r2), hard ? 1.1 : 1.55);
    const d = Math.max(0, lobes * 1.35 - 0.28) * env;
    return Math.min(1, d * 1.9);
  };
  const K = 0.06; // 2-tap derivative offset, light from ABOVE (v decreases upward)
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const u = px / size;
      const v = py / size;
      const d = density(u, v);
      let shade = 1;
      if (lit && d > 0) {
        const dLit = density(u, v - K); // sample toward the light
        shade = Math.min(1, Math.max(0, 0.45 + (d - dLit) * 3.2 + d * 0.25));
      }
      const i = (py * size + px) * 4;
      img.data[i] = Math.round(shade * 255);
      img.data[i + 1] = 0;
      img.data[i + 2] = 0;
      img.data[i + 3] = Math.round(d * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return new CanvasTexture(canvas);
}

// Curved tendril ribbons: quadratic beziers, ridged-FBM alpha, fading tips.
function tendrilTexture(seed) {
  const w = 512;
  const h = 256;
  const canvas = makeCanvas(w, h);
  const ctx = canvas.getContext('2d');
  const rand = seededRandom(seed);
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'lighter';
  for (let k = 0; k < 4; k++) {
    const y0 = h * (0.2 + rand() * 0.6);
    const y1 = h * (0.2 + rand() * 0.6);
    const cx = w * (0.25 + rand() * 0.5);
    const cy = h * (rand() < 0.5 ? -0.25 : 1.25); // control point OFF-canvas = real curvature
    const baseW = 7 + rand() * 12;
    const steps = 64;
    for (let s = 0; s < steps; s++) {
      const t0 = s / steps;
      const t1 = (s + 1) / steps;
      const q = (t, a, b) => {
        const mt = 1 - t;
        return { x: mt * mt * 0 + 2 * mt * t * cx + t * t * w, y: mt * mt * a + 2 * mt * t * cy + t * t * b };
      };
      const p0 = q(t0, y0, y1);
      const p1 = q(t1, y0, y1);
      const taper = Math.sin(Math.PI * t0) ** 1.3; // thick middle, vanishing tips
      const fade = 1 - t0 * 0.55; // brighter at the attachment end
      ctx.strokeStyle = `rgba(255,255,255,${(0.05 + 0.1 * taper) * fade})`;
      ctx.lineWidth = Math.max(0.5, baseW * taper);
      ctx.lineCap = 'round';
      ctx.shadowColor = 'rgba(255,255,255,0.35)';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(p0.x, p0.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.stroke();
    }
  }
  // break the ribbons into wisps with ridged noise, feather the borders
  const img = ctx.getImageData(0, 0, w, h);
  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      const i = (py * w + px) * 4;
      const a = img.data[i + 3] / 255;
      if (a === 0) continue;
      const u = px / w;
      const v = py / h;
      const ridge = 1 - Math.abs(2 * billow(u * 5, v * 9, seed + 7, 4) - 1);
      const dx = (u - 0.5) * 2;
      const dy = (v - 0.5) * 2;
      const env = Math.max(0, 1 - dx * dx) * Math.max(0, 1 - dy * dy);
      img.data[i] = 255; // flat shading channel
      img.data[i + 3] = Math.round(Math.min(1, a * (0.35 + ridge * 0.9)) * env * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return new CanvasTexture(canvas);
}

// ------------------------------------------------------------------ shader
const VERTEX = /* glsl */ `
  attribute vec3 aPos;
  attribute vec2 aScale;
  attribute float aRoll;
  attribute vec3 aColor;
  attribute float aAlpha;
  attribute float aSeed;
  uniform float uTime;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vFade;
  varying float vAlpha;
  void main() {
    vUv = uv;
    vColor = aColor;
    vAlpha = aAlpha;
    vec3 p = aPos;
    p.x += sin(uTime * 0.04 + aSeed * 6.2832) * 0.3;
    p.y += cos(uTime * 0.03 + aSeed * 9.4248) * 0.22;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    // billboard with a small roll only — the baked top-light must stay up
    float roll = aRoll + (aSeed - 0.5) * uTime * 0.012;
    float cr = cos(roll);
    float sr = sin(roll);
    vec2 corner = position.xy * aScale;
    mv.xy += vec2(corner.x * cr - corner.y * sr, corner.x * sr + corner.y * cr);
    float dist = -mv.z;
    vFade = smoothstep(1.8, 5.5, dist) * (1.0 - smoothstep(30.0, 48.0, dist));
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAGMENT = /* glsl */ `
  uniform sampler2D uMap;
  uniform float uShadeLo;
  uniform float uShadeHi;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vFade;
  varying float vAlpha;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    vec3 col = vColor * mix(uShadeLo, uShadeHi, t.r);
    gl_FragColor = vec4(col, t.a * vFade * vAlpha);
  }
`;

// ------------------------------------------------- palette + zone mapping
// Muted astrophoto stops (research: zones blend only at boundaries; low
// luminance per sprite — additive accumulation builds the light).
const STOPS = {
  crimson: new Color('#8a3b3f'), // H-alpha body
  rose: new Color('#925a5e'), // rose fringe toward the video's mauve
  amber: new Color('#c98a4b'), // irradiated core glow
  teal: new Color('#3e6b6b'), // dusty OIII
  indigo: new Color('#1b2a4a'), // deep outer body
  maroon: new Color('#3d1c22'), // fading periphery
  dust: new Color('#2e1f1a'), // occluding dust umber
};

// hue by region — left = crimson/rose (harmonizes with the video), center =
// amber core, right = teal→indigo; low periphery drifts maroon
function zoneColor(x, y, rand, out) {
  const t = Math.min(1, Math.max(0, (x + 9) / 18));
  if (t < 0.38) out.copy(STOPS.crimson).lerp(STOPS.rose, rand() * 0.7);
  else if (t < 0.58) out.copy(STOPS.amber).lerp(STOPS.crimson, 0.35 + rand() * 0.4);
  else if (t < 0.8) out.copy(STOPS.teal).lerp(STOPS.amber, rand() * 0.25);
  else out.copy(STOPS.indigo).lerp(STOPS.teal, rand() * 0.5);
  if (y < -1.6) out.lerp(STOPS.maroon, 0.5);
  return out;
}

// ------------------------------------------------------------ layer builder
function buildLayer({ defs, texture, blending, renderOrder, shadeLo, shadeHi }) {
  const geometry = new PlaneGeometry(1, 1);
  const material = new ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uMap: { value: texture },
      uShadeLo: { value: shadeLo },
      uShadeHi: { value: shadeHi },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending,
  });
  const count = defs.length;
  const mesh = new InstancedMesh(geometry, material, count);
  const pos = new Float32Array(count * 3);
  const scale = new Float32Array(count * 2);
  const roll = new Float32Array(count);
  const col = new Float32Array(count * 3);
  const alp = new Float32Array(count);
  const seed = new Float32Array(count);
  defs.forEach((d, i) => {
    pos.set(d.pos, i * 3);
    scale.set(d.scale, i * 2);
    roll[i] = d.roll;
    col.set(d.color, i * 3);
    alp[i] = d.alpha;
    seed[i] = d.seed;
  });
  geometry.setAttribute('aPos', new InstancedBufferAttribute(pos, 3));
  geometry.setAttribute('aScale', new InstancedBufferAttribute(scale, 2));
  geometry.setAttribute('aRoll', new InstancedBufferAttribute(roll, 1));
  geometry.setAttribute('aColor', new InstancedBufferAttribute(col, 3));
  geometry.setAttribute('aAlpha', new InstancedBufferAttribute(alp, 1));
  geometry.setAttribute('aSeed', new InstancedBufferAttribute(seed, 1));
  mesh.frustumCulled = false;
  mesh.renderOrder = renderOrder;
  return mesh;
}

export function NebulaVolume({ position = [0, 0, 0] }) {
  const groupRef = useRef(null);

  const layers = useMemo(() => {
    const rand = seededRandom(20260713);
    const c = new Color();

    // ---- cauliflower masses: overlapping ellipsoid kernels per mass -------
    // corridor slab, masses kept off the exact flight line so the camera
    // threads a canyon between billowing walls (Cosmic Cliffs reading)
    const masses = [];
    const MASS_SEEDS = [
      [-6.5, 0.6, -4], [-4.2, -1.4, 6], [-2.6, 1.8, 12],
      [2.4, -0.8, 10], [4.6, 1.2, 1], [6.8, -0.6, -6], [3.4, 2.2, -9],
    ];
    for (const [mx, my, mz] of MASS_SEEDS) {
      const kernels = [];
      const kn = 2 + Math.floor(rand() * 3);
      for (let k = 0; k < kn; k++) {
        kernels.push({
          x: mx + (rand() - 0.5) * 2.4,
          y: my + (rand() - 0.5) * 1.6,
          z: mz + (rand() - 0.5) * 2.4,
          r: 1.3 + rand() * 1.5,
        });
      }
      masses.push(kernels);
    }
    const field = (kernels, x, y, z) => {
      let f = 0;
      for (const k of kernels) {
        const dx = x - k.x;
        const dy = y - k.y;
        const dz = z - k.z;
        f += Math.exp(-(dx * dx + dy * dy + dz * dz) / (k.r * k.r));
      }
      return f;
    };

    const glow = [];
    for (const kernels of masses) {
      let placed = 0;
      let guard = 0;
      while (placed < 20 && guard < 120) {
        guard++;
        const k = kernels[Math.floor(rand() * kernels.length)];
        const x = k.x + (rand() * 2 - 1) * k.r * 1.5;
        const y = k.y + (rand() * 2 - 1) * k.r * 1.1;
        const z = k.z + (rand() * 2 - 1) * k.r * 1.5;
        const f = field(kernels, x, y, z);
        if (f < 0.45) continue; // rejection: puffs only inside the mass
        placed++;
        const size = (0.9 + Math.min(1.6, f) * 1.1) * (0.8 + rand() * 0.5);
        zoneColor(x, y, rand, c);
        glow.push({
          pos: [x, y, z],
          scale: [rand() < 0.5 ? -size : size, size * (0.82 + rand() * 0.3)],
          roll: (rand() - 0.5) * 0.5,
          color: [c.r, c.g, c.b],
          alpha: 0.07 + rand() * 0.09,
          seed: rand(),
        });
      }
    }
    // faint huge ambient veils so structure doesn't float on empty black
    for (let i = 0; i < 8; i++) {
      const x = (rand() * 2 - 1) * 9;
      const y = (rand() * 2 - 1) * 3.2;
      zoneColor(x, y, rand, c);
      glow.push({
        pos: [x, y, (rand() * 2 - 1) * 12 + 2],
        scale: [12 + rand() * 7, 7 + rand() * 4],
        roll: (rand() - 0.5) * 0.4,
        color: [c.r, c.g, c.b],
        alpha: 0.035 + rand() * 0.025,
        seed: rand(),
      });
    }

    // ---- dark dust: occluding umber blobs + a few small "globules" --------
    const dust = [];
    for (let i = 0; i < 26; i++) {
      const big = rand() < 0.65;
      const kernels = masses[Math.floor(rand() * masses.length)];
      const k = kernels[Math.floor(rand() * kernels.length)];
      const size = big ? 1.6 + rand() * 2.2 : 0.5 + rand() * 0.5;
      dust.push({
        pos: [k.x + (rand() - 0.5) * 3, k.y + (rand() - 0.5) * 2 - 0.4, k.z + (rand() - 0.5) * 3 + 0.5],
        scale: [rand() < 0.5 ? -size : size, size * (0.7 + rand() * 0.4)],
        roll: (rand() - 0.5) * 0.6,
        color: [STOPS.dust.r, STOPS.dust.g, STOPS.dust.b],
        alpha: big ? 0.3 + rand() * 0.2 : 0.5 + rand() * 0.25,
        seed: rand(),
      });
    }

    // ---- curved tendrils: attached to masses, arcing off the billow tops --
    const tendrils = [];
    for (let i = 0; i < 22; i++) {
      const kernels = masses[Math.floor(rand() * masses.length)];
      const k = kernels[Math.floor(rand() * kernels.length)];
      const len = 3.5 + rand() * 4;
      const x = k.x + (rand() - 0.5) * 2;
      const y = k.y + k.r * (0.4 + rand() * 0.7); // off the crest, toward the "star"
      zoneColor(x, y, rand, c);
      tendrils.push({
        pos: [x, y, k.z + (rand() - 0.5) * 2],
        scale: [rand() < 0.5 ? -len : len, len * (0.28 + rand() * 0.14)],
        roll: (rand() - 0.5) * 0.9,
        color: [c.r, c.g, c.b],
        alpha: 0.1 + rand() * 0.1,
        seed: rand(),
      });
    }

    return [
      buildLayer({ defs: dust, texture: puffTexture(4021, { lit: false, hard: true }), blending: NormalBlending, renderOrder: 1, shadeLo: 1, shadeHi: 1 }),
      buildLayer({ defs: glow, texture: puffTexture(1187, { lit: true }), blending: AdditiveBlending, renderOrder: 2, shadeLo: 0.3, shadeHi: 1.45 }),
      buildLayer({ defs: tendrils, texture: tendrilTexture(733), blending: AdditiveBlending, renderOrder: 3, shadeLo: 1, shadeHi: 1 }),
    ];
  }, []);

  // <primitive> does not auto-dispose; journey remounts on every mode toggle
  useEffect(() => {
    return () => {
      for (const mesh of layers) {
        mesh.geometry.dispose();
        mesh.material.uniforms.uMap.value.dispose();
        mesh.material.dispose();
      }
    };
  }, [layers]);

  useFrame((_, dt) => {
    for (const mesh of layers) mesh.material.uniforms.uTime.value += dt;
  });

  return (
    <group ref={groupRef} position={position}>
      {layers.map((mesh, i) => (
        <primitive key={i} object={mesh} />
      ))}
    </group>
  );
}
